import { useState, useRef, useEffect } from "react";
import FileRow from "./FileRow";
import { dateTimeFormatter } from "../../utils/dateTimeFormatter";
import axios from "axios";
import FileList from "./FileList";
import SuccessPopup from "../shared/SuccessPopup";
import { useClipboard } from "../../context/MoveCopyContext";
import ConfirmPopup from "../shared/ConfirmPopup";
import { useDocStorePermission } from "../Docstore/hooks/useDocStorePermission.ts";
import { formatBytes, normalizeType } from "../utils/generalUtilities.js";
import { DocstoreType } from "../../utils/docstoreTabs.js";
import FilePreviewDialog from "./FilePreviewDialog.jsx";
import FileCard from "./FileCard.jsx";
import userFolderImage from "../../assets/Images/user-folder-image.png";
import userTeamFolderImage from "../../assets/Images/user-team-folder-image.png";
import userShareFolderImage from "../../assets/Images/user-share-folder-image.png";
import workSpaceImage from "../../assets/Images/workspace.png"
import { toast } from "react-toastify";
import { truncateText } from "../../utils/textTruncateUtils.js";
import { navigateToOfficeViewer } from "../../services/wopi.service.js";
import pasteIcon from '../../assets/Images/paste.png';
import renameIcon from '../../assets/Images/rename.png';

import foldermoveIcon from '../../assets/Images/send.png';

const FolderList = ({
  folders,
  files,
  onFolderClick,
  activeTab,
  currentCoreOpenTab,
  setCurrentCoreOpenTab,
  currentFolderId,
  loadFolderContent,
  mebFiles,
  ladFiles,
  edFiles,
  docStorePermissions,
  fetchStorageInfo,
  viewMode,
  isAdmin,
  selectedUser,
  setSelectedUser
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const wopiBaseUrl = process.env.REACT_APP_WOPI_BASE_URL;
  const accessToken = sessionStorage.getItem("accessToken");
  const headers = accessToken ? { Authorization: accessToken } : {};
  const [modalState, setModalState] = useState({
    isOpen: false,
    folder: null,
    newName: "",
  });
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState({
    type: "",
    show: false,
    message: "",
  });
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    fileName: "",
    fileUrl: "",
    extension: "",
  });
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    folderId: null,
  });
  const contextMenuRef = useRef(null);
  const { clipboardItem, clearClipboard, setClipboardItem } = useClipboard();
  const hasPermission = useDocStorePermission(docStorePermissions);

  useEffect(() => {
    const handleClickOutside = (e) => {
      setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleToggleMenu = (e, folderId) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();

    window.dispatchEvent(new Event("closeFileContextMenu"));

    setContextMenu({
      visible: true,
      x: rect.left,
      y: rect.bottom + window.scrollY,
      folderId,
    });
  };

  useEffect(() => {
    const handler = () =>
      setContextMenu((prev) => ({ ...prev, visible: false }));
    window.addEventListener("closeFolderContextMenu", handler);
    return () => window.removeEventListener("closeFolderContextMenu", handler);
  }, []);

  const handleOpen = (folder) => {
    if (folder.isVirtualUserFolder) {
      setSelectedUser(folder.username);
      return;
    }
    if (folder.isCoreTab) {
      setCurrentCoreOpenTab?.(folder.id);
    } else {
      onFolderClick(folder);
      setCurrentCoreOpenTab?.(null);
    }
  };
  const handleRename = (folder) => {
    setModalState({
      isOpen: true,
      folder,
      newName: folder.name,
    });
  };

  // close without saving
  const handleCloseModal = () =>
    setModalState({ isOpen: false, folder: null, newName: "" });

  // keep input controlled
  const handleChange = (e) =>
    setModalState((s) => ({ ...s, newName: e.target.value }));

  const handleRenameSubmit = async (folderId, newName) => {
    try {
      const response = await axios.put(
        `${baseUrl}/storage/folder/${folderId}/rename`,
        { name: newName },
        { headers }
      );

      if (response.status === 200) {
        loadFolderContent(currentFolderId);
        setShowSuccessPopup({
          type: "success",
          show: true,
          message: "Folder renamed successfully",
        });
      } else {
        setShowSuccessPopup({
          type: "warning",
          show: true,
          message: "Rename failed. Please try again",
        });
      }
    } catch (err) {
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "An error occurred while renaming the folder.",
      });
    } finally {
      setContextMenu({ visible: false, folderId: null, x: 0, y: 0 });
    }
  };

  const onRenameClick = () => {
    const trimmed = modalState.newName.trim();
    if (!trimmed || trimmed === modalState.folder.name) return; // noop
    handleRenameSubmit(modalState.folder.id, trimmed);
    handleCloseModal();
  };

  const handleDelete = async (folder) => {
    setShowConfirmPopup(true);
    setSelectedFolder(folder);
    fetchStorageInfo();
  };

  const handleConfirmDelete = async () => {
    if (!selectedFolder) return;
    try {
      const response = await axios.delete(
        `${baseUrl}/storage/folder/${selectedFolder.id}`,
        { headers }
      );
      if (response.status === 204) {
        loadFolderContent(currentFolderId);
        setShowConfirmPopup(false);
        toast.success(`Folder deleted successfully`, {
          autoClose: 5000,
          closeButton: true,
          theme: "colored",
        });
      } else {
        toast.error(`Delete failed`, {
          autoClose: 5000,
          theme: "colored",
        });
      }
    } catch (err) {
      toast.error(`An error occurred while deleting the folder`, {
        autoClose: 5000,
        theme: "colored",
      });
    }
    setContextMenu({ visible: false, folderId: null, x: 0, y: 0 });
  };

  const handlePasteHere = async (targetFolderId, targetDocStoreType) => {
    if (!clipboardItem || !["move", "copy"].includes(clipboardItem.operation))
      return;

    if (
      clipboardItem.type === "folder" &&
      clipboardItem.id === targetFolderId
    ) {
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "Cannot paste folder into itself.",
      });
      return;
    }

    if (
      clipboardItem.docStoreType &&
      normalizeType(clipboardItem.docStoreType) !== normalizeType(targetDocStoreType)
    ) {
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "Copy/Move is only allowed between the same DocStore types.",
      });
      return;
    }

    try {
      const userId = parseInt(localStorage.getItem("id"));
      const isSystemLevel = DocstoreType[activeTab] !== "USER_FILES";
      const systemId = localStorage.getItem("selectedSystemId");

      if (clipboardItem.type === "file") {
        await axios.put(
          `${baseUrl}/storage/file/${clipboardItem.operation}`,
          {
            fileId: clipboardItem.id,
            newFolderId: targetFolderId,
            userId,
            systemId: systemId,
            isSystemLevel,
            docstoreType: DocstoreType[activeTab],
          },
          { headers }
        );
      } else if (clipboardItem.type === "folder") {
        await axios.put(
          `${baseUrl}/storage/folder/${clipboardItem.operation}`,
          {
            folderId: clipboardItem.id,
            newParentId: targetFolderId,
            userId,
            systemId: systemId,
            isSystemLevel,
            docstoreType: DocstoreType[activeTab],
          },
          { headers }
        );
      }
      setShowSuccessPopup({
        type: "success",
        show: true,
        message: `${
          clipboardItem.operation === "move" ? "Moved" : "Copied"
        } successfully.`,
      });
      clearClipboard();
      loadFolderContent(currentFolderId);
    } catch (err) {
      let errorMessage = "Failed to paste item.";
      if (err?.response?.data?.details) {
        errorMessage += ` ${err.response.data.details}`;
        errorMessage = errorMessage.replace(".", ",");
      }
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: errorMessage,
      });
    } finally {
      setContextMenu({ visible: false, folderId: null, x: 0, y: 0 });
    }
  };

  const handleCut = (folder) => {
    setClipboardItem({
      type: "folder",
      id: folder.id,
      name: folder.name,
      operation: "move",
      docStoreType: activeTab
    });
    setContextMenu({ visible: false, folderId: null, x: 0, y: 0 });
  };

  let filteredFiles =
    files?.filter((file) => file.folderId === currentFolderId) || [];

  const isInRootDirectory = !currentCoreOpenTab && !currentFolderId;

  let visibleFolders = folders.filter((folder) => {
    if (isInRootDirectory) return true; // show all folders in root
    if (currentCoreOpenTab) return folder.isCoreTab;
    return !folder.isCoreTab;
  });

  const handleView = (fileKey, fileName, extension) => {
    if (!fileKey || !accessToken) return;

    const encodedKey = encodeURIComponent(fileKey);
    const viewUrl = `${wopiBaseUrl}/api/api/file/download?key=${encodedKey}`;

    setPreviewModal({
      isOpen: true,
      fileName: fileName,
      fileUrl: viewUrl,
      extension: extension,
    });

    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  const groupByUser = (folders, files) => {
    const grouped = {};
    folders.forEach(f => {
      if (!grouped[f.username]) {
        grouped[f.username] = { userId: f.userId, folders: [], files: [], size: 0 };
      }
      grouped[f.username].folders.push(f);
      grouped[f.username].size += f.size || 0;
    });
    files.forEach(file => {
      if (!grouped[file.username]) {
        grouped[file.username] = { userId: file.userId, folders: [], files: [], size: 0 };
      }
      grouped[file.username].files.push(file);
      grouped[file.username].size += file.size || 0;
    });
    return grouped;
  };

  const grouped = groupByUser(folders, files);

  if (activeTab === "User Files" && isAdmin) {
    if (!selectedUser) {
      visibleFolders = Object.keys(grouped).map(username => ({
        id: `user-${grouped[username].userId}`,
        name: `${username}_workspace`,
        isVirtualUserFolder: true,
        username,
        size: grouped[username].size,
      }));
      filteredFiles = [];
    } else {
      visibleFolders = grouped[selectedUser]?.folders || [];
      filteredFiles = grouped[selectedUser]?.files || [];
    }
  } else {
    filteredFiles = files?.filter(file => file.folderId === currentFolderId) || [];

    const isInRootDirectory = !currentCoreOpenTab && !currentFolderId;
    visibleFolders = folders.filter(folder => {
      if (isInRootDirectory) return true;
      if (currentCoreOpenTab) return folder.isCoreTab;
      return !folder.isCoreTab;
    });
  }

  return (
    <>
      {viewMode === "table" ? (
        <table className="table align-middle mb-0">
          <thead className="bg-light text-secondary small">
            <tr>
              <th className="ps-4">Name</th>
              <th>Uploaded By</th>
              <th>Type/Extension</th>
              <th>Size</th>
              <th>Modified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleFolders.length > 0 || files?.length > 0 ? (
              visibleFolders.map((folder) => {
                const isCoreTab = folder.isCoreTab;

                if (isCoreTab && folder.id === currentCoreOpenTab) {
                  if (
                    activeTab === "Engineering Drawings" &&
                    edFiles.length > 0
                  ) {
                    return edFiles.map((file) => (
                      <tr
                        key={file.id}
                        style={{ cursor: "pointer" }}
                        className="fnt14"
                        onDoubleClick={() =>
                          handleView(
                            file?.generated_image?.key,
                            file?.generated_image?.name,
                            file?.extension
                          )
                        }
                      >
                        <td className="ps-4">
                          <i className="bi bi-file-earmark-image me-2 text-secondary fnt14" />
                          {file.generated_image.key.split(".")[0]}
                        </td>
                        <td>Admin</td>
                        <td>{file.generated_image.type}</td>
                        <td>{formatBytes(file.generated_image.size)}</td>
                        <td>{dateTimeFormatter(file.updated_at)}</td>
                        <td>--</td>
                      </tr>
                    ));
                  }
                  if (activeTab === "LAD" && ladFiles.length > 0) {
                    return ladFiles.map((file) => (
                      <tr
                        key={file.id}
                        style={{ cursor: "pointer" }}
                        className="fnt14"
                        onDoubleClick={() =>
                          handleView(
                            `${file?.key}.png`,
                            file?.name,
                            file?.extension
                          )
                        }
                      >
                        <td className="ps-4">
                          <i className="bi bi-file-earmark-image me-2 text-secondary fnt14" />
                          {file.name.split(".")[0]}
                        </td>
                        <td>Admin</td>
                        <td>image/{file.name.split(".")[1]}</td>
                        <td>{formatBytes(file.size)}</td>
                        <td>{dateTimeFormatter(file.updated_at)}</td>
                        <td>--</td>
                      </tr>
                    ));
                  }

                  if (activeTab === "MEB" && mebFiles.length > 0) {
                    return mebFiles.map((file) => (
                      <tr
                        key={file.id}
                        style={{ cursor: "pointer" }}
                        className="fnt14"
                        onDoubleClick={() =>
                          handleView(
                            file?.key,
                            file?.title,
                            'docx'
                          )
                        }
                        //  onDoubleClick={() => navigateToOfficeViewer(file?.key)}
                      >
                        <td className="ps-4">
                          <i className="bi bi-file-earmark-text me-2 text-secondary fnt14" />
                          {file.title.split(".")[0]}
                        </td>
                        <td>Admin</td>
                        <td> {file.title.split(".")[1]}</td>
                        <td>{formatBytes(file.size)}</td>
                        <td>{dateTimeFormatter(file.created_at)}</td>
                        <td>--</td>
                      </tr>
                    ));
                  } else {
                    return (
                      <tr>
                        <td className="text-center fnt14" colSpan={5}>
                          No documents to display.
                        </td>
                      </tr>
                    );
                  }
                }

                return (
                  <FileRow
                    key={`folder-${folder.id}`}
                    icon={
                      isCoreTab ? (
                        <i className="bi bi-database-fill text-info fs-5" />
                      ) : (
                        <img
                          src={
                            folder.isVirtualUserFolder
                            ? workSpaceImage 
                            : folder.folderType === "Team folder"
                              ? userTeamFolderImage
                              : folder.folderType === "Share folder"
                              ? userShareFolderImage
                              : userFolderImage
                          }
                          alt="folder"
                          style={{
                            width: folder.isVirtualUserFolder ? "34px" : "20px",
                            height: folder.isVirtualUserFolder ? "30px" : "16px",
                            objectFit: "contain"
                          }}                        />
                      )
                    }
                    name={folder.name}
                    user={folder?.username}
                    modified={
                      folder?.updatedAt
                        ? dateTimeFormatter(folder.updatedAt)
                        : "--"
                    }
                    extension={""}
                    size={!isCoreTab ? formatBytes(folder.size) : "--"}
                    actions={
                      folder.isCoreTab ? (
                        <button
                          className="btn btn-sm border-0"
                          style={{ cursor: "not-allowed" }}
                          disabled
                        >
                          <i className="bi bi-three-dots-vertical breadcrumb-text-decoration" />
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm"
                            onClick={(e) => handleToggleMenu(e, folder.id)}
                          >
                            <i className="bi bi-three-dots-vertical breadcrumb-text-decoration" />
                          </button>
                          {contextMenu.visible &&
                            contextMenu.folderId === folder.id && (
                              <ul
                                ref={contextMenuRef}
                                className="position-absolute theme-container border shadow-sm list-unstyled fnt14"
                                style={{
                                  top: contextMenu.y,
                                  left: contextMenu.x,
                                  zIndex: 9999,
                                  padding: "0.6rem",
                                  minWidth: "120px",
                                  cursor: "pointer",
                                }}
                              >
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("VIEW")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => handleOpen(folder)}
                                >
                                  <i className="bi bi-folder2-open"></i>
                                  &nbsp;Open
                                </li>
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("MOVE")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (
                                      activeTab !== "User Files" &&
                                      !hasPermission("MOVE")
                                    )
                                      return;
                                    handleCut(folder);
                                  }}
                                >
                                  <img
                                  src={foldermoveIcon} 
                                  alt="move"
                                  width="14"
                                  height="14"
                                  className="context-menu-icon"
                                  />
                                  &nbsp;Move
                                </li>
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("RENAME")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (
                                      activeTab !== "User Files" &&
                                      !hasPermission("RENAME")
                                    )
                                      return;
                                    handleRename(folder);
                                  }}
                                >
                                   <img
                                   src={renameIcon} 
                                   alt="Rename"
                                   width="14"
                                   height="17"
                                   className="context-menu-icon"
                                 />&nbsp;Rename
                                </li>
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item text-danger ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("DELETE")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (
                                      activeTab !== "User Files" &&
                                      !hasPermission("DELETE")
                                    )
                                      return;
                                    handleDelete(folder);
                                  }}
                                >
                                  <i className="bi bi-trash3"></i>&nbsp;Delete
                                </li>
                                {clipboardItem && (
                                  <li
                                    className={`dropdown-item text-success ps-1 ${
                                      (clipboardItem.operation === "MOVE" &&
                                        activeTab !== "User Files" &&
                                        !hasPermission("MOVE")) ||
                                      (clipboardItem.operation === "COPY" &&
                                        activeTab !== "User Files" &&
                                        !hasPermission("COPY"))
                                        ? "disabled text-muted"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      const canPaste =
                                        (clipboardItem.operation === "MOVE" &&
                                          activeTab !== "User Files" &&
                                          hasPermission("MOVE")) ||
                                        (clipboardItem.operation === "COPY" &&
                                          activeTab !== "User Files" &&
                                          hasPermission("COPY"));

                                      if (canPaste) return;
                                      handlePasteHere(
                                        folder.id,
                                        folder.docstoreType
                                      );
                                    }}
                                  >

                                    <img
                                     src={pasteIcon} 
                                     alt="Paste"
                                     width="14"
                                     height="15"
                                     className="context-menu-icon"
                                    />
                                    &nbsp;Paste
                                  </li>
                                )}
                              </ul>
                            )}
                        </>
                      )
                    }
                    onClick={() => handleOpen(folder)}
                  />
                );
              })
            ) : (
              <tr>
                <td className="text-center fnt14" colSpan={5}>
                  This folder is empty.
                </td>
              </tr>
            )}

            {/* Render regular files only if not in core tab */}
            {!currentCoreOpenTab && (
              <FileList
                files={filteredFiles}
                refresh={() => loadFolderContent(currentFolderId)}
                docStorePermissions={docStorePermissions}
                activeTab={activeTab}
                fetchStorageInfo={fetchStorageInfo}
                viewMode={viewMode}
              />
            )}
          </tbody>
        </table>
      ) : (
        <div className="d-flex flex-wrap justify-content-start gap-1">
          {visibleFolders.length > 0 || files?.length > 0 ? (
            visibleFolders.map((folder) => {
              if (folder?.isCoreTab && folder.id === currentCoreOpenTab) {
                if (
                  activeTab === "Engineering Drawings" &&
                  edFiles.length > 0
                ) {
                  return edFiles.map((file) => (
                    <div
                      key={file.id}
                      style={{ cursor: "pointer" }}
                      className="fnt14"
                      onDoubleClick={() =>
                        handleView(
                          file?.generated_image?.key,
                          file?.generated_image?.name,
                          file?.extension
                        )
                      }
                    >
                      <div
                        className="card doc-store-list-view-bg mb-2"
                        style={{ width: "130px", height: "105px" }}
                      >
                        <div className="d-flex flex-column align-items-center justify-content-center h-100">
                          <div className="py-2">
                            <i className="bi bi-file-earmark-image me-2 text-secondary fnt30" />
                          </div>
                        </div>
                        <div
                          className="border-top border-secondary w-100 d-flex justify-content-between align-items-center px-1 py-1 fnt14"
                          title={file.generated_image.key}
                        >
                          {truncateText(file.generated_image.key, 12)}
                        </div>
                      </div>
                    </div>
                  ));
                }
                if (activeTab === "LAD" && ladFiles.length > 0) {
                  return ladFiles.map((file) => (
                    <div
                      key={file.id}
                      style={{ cursor: "pointer" }}
                      className="fnt14"
                      onDoubleClick={() =>
                        handleView(
                          `${file?.key}.png`,
                          file?.name,
                          file?.extension
                        )
                      }
                    >
                      <div
                        className="card doc-store-list-view-bg mb-2"
                        style={{ width: "130px", height: "105px" }}
                      >
                        <div className="d-flex flex-column align-items-center justify-content-center h-100">
                          <div className="py-2">
                            <i className="bi bi-file-earmark-image me-2 text-secondary fnt30" />
                          </div>
                        </div>
                        <div
                          className="border-top border-secondary w-100 d-flex justify-content-between align-items-center px-1 py-1 fnt14"
                          title={file.name}
                        >
                          {truncateText(file.name, 12)}
                        </div>
                      </div>
                    </div>
                  ));
                }

                if (activeTab === "MEB" && mebFiles.length > 0) {
                  return mebFiles.map((file) => (
                    <div
                      key={file.id}
                      style={{ cursor: "pointer" }}
                      className="fnt14"
                      onDoubleClick={() =>
                        handleView(
                          file?.key,
                          file?.title,
                          'docx'
                        )
                      }
                    >
                      <div
                        className="card doc-store-list-view-bg mb-2"
                        style={{ width: "130px", height: "105px" }}
                      >
                        <div className="d-flex flex-column align-items-center justify-content-center h-100">
                          <div className="py-2">
                            <i className="bi bi-file-earmark-text me-2 text-secondary fnt30" />
                          </div>
                        </div>
                        <div
                          className="border-top border-secondary w-100 d-flex justify-content-between align-items-center px-1 py-1 fnt14"
                          title={file.title}
                        >
                          {truncateText(file.title, 12)}
                        </div>
                      </div>
                    </div>
                  ));
                } else {
                  return (
                    <div className="row w-100 py-2">
                      <div className="col-md-12 text-center fnt14">
                        No documents to display.
                      </div>
                    </div>
                  );
                }
              }

              return (
                <div className="folder-list-card" key={folder.id}>
                  <FileCard
                    key={`folder-${folder.id}`}
                    icon={
                      folder?.isCoreTab ? (
                        <i className="bi bi-database-fill text-info fnt30" />
                      ) : (
                        <img
                          src={
                            folder.isVirtualUserFolder
                            ? workSpaceImage 
                            : folder.folderType === "Team folder"
                              ? userTeamFolderImage
                              : folder.folderType === "Share folder"
                              ? userShareFolderImage
                              : userFolderImage
                          }
                          alt="folder"
                          style={{
                            width: folder.isVirtualUserFolder ? "66px" : "40px",
                            height: folder.isVirtualUserFolder ? "54px" : "30px",
                            objectFit: "contain"
                          }}
                        />
                      )
                    }
                    name={folder.name}
                    user={folder?.username}
                    modified={
                      folder?.updatedAt
                        ? dateTimeFormatter(folder.updatedAt)
                        : "--"
                    }
                    size={!folder?.isCoreTab ? formatBytes(folder.size) : "--"}
                    actions={
                      folder.isCoreTab ? (
                        <button
                          className="btn btn-sm border-0"
                          style={{ cursor: "not-allowed" }}
                          disabled
                        >
                          <i className="bi bi-three-dots-vertical breadcrumb-text-decoration" />
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm"
                            onClick={(e) => handleToggleMenu(e, folder.id)}
                          >
                            <i className="bi bi-three-dots-vertical breadcrumb-text-decoration" />
                          </button>
                          {contextMenu.visible &&
                            contextMenu.folderId === folder.id && (
                              <ul
                                ref={contextMenuRef}

                                className="theme-container border shadow-sm list-unstyled fnt14"
                                style={{
                                  position: "absolute",
                                  // top: contextMenu.y,
                                  // left: contextMenu.x,
                                  zIndex: 9999999999999,
                                  padding: "0.5rem",
                                  minWidth: "120px",
                                  cursor: "pointer",
                                }}
                              >
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("VIEW")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => handleOpen(folder)}
                                >
                                  <i className="bi bi-folder2-open"></i>
                                  &nbsp;Open
                                </li>
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("MOVE")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (
                                      activeTab !== "User Files" &&
                                      !hasPermission("MOVE")
                                    )
                                      return;
                                    handleCut(folder);
                                  }}
                                >
                                   <img
                                  src={foldermoveIcon} 
                                  alt="move"
                                  width="14"
                                  height="14"
                                  className="context-menu-icon"
                                  />
                                  &nbsp;Move
                                </li>
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("RENAME")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (
                                      activeTab !== "User Files" &&
                                      !hasPermission("RENAME")
                                    )
                                      return;
                                    handleRename(folder);
                                  }}
                                >
                                  <img
                                  src={renameIcon} 
                                  alt="Rename"
                                  width="14"
                                  height="17"
                                  className="context-menu-icon"
                                  />&nbsp;Rename
                                </li>
                                <li
                                  className={`d-flex align-items-center py-1 ps-1 dropdown-item text-danger ${
                                    activeTab !== "User Files" &&
                                    !hasPermission("DELETE")
                                      ? "disabled text-muted"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (
                                      activeTab !== "User Files" &&
                                      !hasPermission("DELETE")
                                    )
                                      return;
                                    handleDelete(folder);
                                  }}
                                >
                                  <i className="bi bi-trash3"></i>&nbsp;Delete
                                </li>
                                {clipboardItem && (
                                  <li
                                    className={`dropdown-item text-success ps-1 ${
                                      (clipboardItem.operation === "MOVE" &&
                                        activeTab !== "User Files" &&
                                        !hasPermission("MOVE")) ||
                                      (clipboardItem.operation === "COPY" &&
                                        activeTab !== "User Files" &&
                                        !hasPermission("COPY"))
                                        ? "disabled text-muted"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      const canPaste =
                                        (clipboardItem.operation === "MOVE" &&
                                          activeTab !== "User Files" &&
                                          hasPermission("MOVE")) ||
                                        (clipboardItem.operation === "COPY" &&
                                          activeTab !== "User Files" &&
                                          hasPermission("COPY"));

                                      if (canPaste) return;
                                      handlePasteHere(
                                        folder.id,
                                        folder.docstoreType
                                      );
                                    }}
                                  >
                                    <img
                                     src={pasteIcon} 
                                     alt="Paste"
                                     width="14"
                                     height="15"
                                     className="context-menu-icon"
                                   /> &nbsp;Paste
                                  </li>
                                )}
                              </ul>
                            )}
                        </>
                      )
                    }
                    onClick={() => handleOpen(folder)}
                  />
                </div>
              );
            })
          ) : (
            <div className="row w-100 py-2">
              <div className="col-12 text-secondary text-center mt-2">
                This folder is empty.
              </div>
            </div>
          )}

          {!currentCoreOpenTab && (
            <FileList
              files={filteredFiles}
              refresh={() => loadFolderContent(currentFolderId)}
              docStorePermissions={docStorePermissions}
              activeTab={activeTab}
              fetchStorageInfo={fetchStorageInfo}
              viewMode={viewMode}
            />
          )}
        </div>
      )}

      {modalState.isOpen && (
        <div
          className="modal fade modal-save-as show show-modal modal--opacity"
          id="docstore-rename-folder-form"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="staticBackdropLabel"
          aria-hidden="true"
          style={{ display: "block" }}
        >
          <div className="modal-dialog" style={{ minWidth: "400px" }}>
            <div className="modal-content">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  {/* Folder icon box */}
                  <div
                    style={{
                      border: "1px solid #f28c48",
                      borderRadius: "8px",
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                    }}
                  >
                    <i
                      className="bi bi-folder-fill"
                      style={{ fontSize: "1.5rem", color: "#1c88bf" }}
                    ></i>
                  </div>

                  {/* Title + Subtitle */}
                  <div>
                    <div className="fnt14 fw-600">Rename Folder</div>
                    <div className="fnt12" style={{ color: "#f28c48" }}>
                      All fields are mandatory *
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close px-1"
                  data-bs-dismiss="modal"
                  onClick={handleCloseModal}
                />
              </div>

              <div className="modal-body">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control floatingInput"
                    id="renameFolder"
                    name="renameFolder"
                    value={modalState.newName}
                    onChange={handleChange}
                    placeholder=""
                    autoFocus
                  />
                  <label className="floatingInput-label" htmlFor="customer">
                    Enter new folder name
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                  onClick={onRenameClick}
                >
                  Rename
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewModal.isOpen && (
        <FilePreviewDialog
          file={{
            fileName: previewModal.fileName,
            fileUrl: previewModal.fileUrl,
          }}
          onClose={() =>
            setPreviewModal({
              isOpen: false,
              fileName: "",
              fileUrl: "",
              extension: "",
            })
          }
          docStorePermissions={docStorePermissions}
        />
      )}

      <SuccessPopup
        type={showSuccessPopup?.type}
        show={showSuccessPopup?.show}
        message={showSuccessPopup?.message}
        onClose={() => setShowSuccessPopup(false)}
      />

      <ConfirmPopup
        show={showConfirmPopup}
        message="Are you sure you want to delete this folder?"
        onCancel={() => setShowConfirmPopup(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default FolderList;
